import { t } from "@impero-com/common/utils/i18n";
import { queryClient } from "@impero-com/common/utils/query_client";
import { emitNotification } from "@impero-com/ui";
import { useSuspenseQuery } from "@tanstack/react-query";

import { getSavedReports } from "api/generated/reports/reports";
import {
    getLegacyClassificationEditAccessRightsQueryKey,
    useLegacyClassificationClassificationsSuspense,
    useLegacyClassificationDelete,
    useLegacyClassificationEditAccessRightsSuspense,
    useLegacyClassificationUpdateAccessRights,
} from "api/legacy/legacy-classification/legacy-classification";
import {
    getLegacyReportEditAccessRightsQueryKey,
    legacyReportIndex,
    useLegacyReportDeleteSavedControlResultStatus,
    useLegacyReportEditAccessRightsSuspense,
    useLegacyReportUpdateAccessRights,
} from "api/legacy/legacy-report/legacy-report";

import {
    convertLegacyClassificationsForUserToClassificationsForUser,
    convertLegacyDataSheetReportToReport,
    convertSavedReportListDtoToReports,
    convertLegacyAccessUsersToEditorData,
} from "./utils";

const REPORTS_QUERY_KEY = "reports";
const CLASSIFICATIONS_QUERY_KEY = "classifications";

const useReportsQuery = () =>
    useSuspenseQuery({
        queryKey: [REPORTS_QUERY_KEY],
        queryFn: () => {
            // Status, Workload and Classification reports are served by V2 API
            const v2Promise = getSavedReports();
            // DataSheet reports are still served by the Legacy API
            const legacyPromise = legacyReportIndex();
            return Promise.all([v2Promise, legacyPromise]);
        },
        select: ([v2Response, legacyResponse]) => {
            const savedReports = convertSavedReportListDtoToReports(
                v2Response.data
            );
            const dataSheetReports = legacyResponse.data.DataSheetReports.map(
                convertLegacyDataSheetReportToReport
            );
            return [...savedReports, ...dataSheetReports];
        },
    });

const useDeleteSavedReportMutation = () =>
    useLegacyReportDeleteSavedControlResultStatus({
        mutation: {
            onSuccess: () => {
                emitNotification("success", t("Report_Action_Delete_Succeed"));
                queryClient.invalidateQueries({
                    queryKey: [REPORTS_QUERY_KEY],
                });
            },
            onError: () => {
                emitNotification("error", t("Report_Action_Delete_Failed"));
            },
        },
    });

const useSavedReportAccessRightsQuery = (savedReportId: string) =>
    useLegacyReportEditAccessRightsSuspense(savedReportId, {
        query: {
            select: (response) =>
                convertLegacyAccessUsersToEditorData(response.data),
        },
    });

const useUpdateSavedReportAccessRightsMutation = () =>
    useLegacyReportUpdateAccessRights({
        mutation: {
            onSuccess: (_data, { id }) => {
                void queryClient.invalidateQueries({
                    queryKey: [REPORTS_QUERY_KEY],
                });
                void queryClient.invalidateQueries({
                    queryKey: getLegacyReportEditAccessRightsQueryKey(id),
                });
            },
        },
    });

const useClassificationsQuery = () =>
    useLegacyClassificationClassificationsSuspense({
        query: {
            queryKey: [CLASSIFICATIONS_QUERY_KEY],
            select: (response) =>
                convertLegacyClassificationsForUserToClassificationsForUser(
                    response.data
                ),
        },
    });

const useDeleteClassificationMutation = () =>
    useLegacyClassificationDelete({
        mutation: {
            onSuccess: () => {
                emitNotification(
                    "success",
                    t("Classification_Action_Delete_Succeed")
                );
                void queryClient.invalidateQueries({
                    queryKey: [CLASSIFICATIONS_QUERY_KEY],
                });
            },
            onError: () => {
                emitNotification(
                    "error",
                    t("Classification_Action_Delete_Failed")
                );
            },
        },
    });

const useClassificationAccessRightsQuery = (classificationId: string) =>
    useLegacyClassificationEditAccessRightsSuspense(classificationId, {
        query: {
            select: (response) =>
                convertLegacyAccessUsersToEditorData(response.data),
        },
    });

const useUpdateClassificationAccessRightsMutation = () =>
    useLegacyClassificationUpdateAccessRights({
        mutation: {
            onSuccess: (_data, { classificationId }) => {
                void queryClient.invalidateQueries({
                    queryKey: [CLASSIFICATIONS_QUERY_KEY],
                });
                void queryClient.invalidateQueries({
                    queryKey:
                        getLegacyClassificationEditAccessRightsQueryKey(
                            classificationId
                        ),
                });
            },
        },
    });

export {
    useReportsQuery,
    useDeleteSavedReportMutation,
    useSavedReportAccessRightsQuery,
    useUpdateSavedReportAccessRightsMutation,
    useClassificationsQuery,
    useDeleteClassificationMutation,
    useClassificationAccessRightsQuery,
    useUpdateClassificationAccessRightsMutation,
};
